import { useState } from 'react';
import { getGseaPayload, createGseaInfoContent } from '../../../components/AdvancedSearch/helpers/helpers';
import { trackEnrichmentAction } from '../../../utils/analytics';
import { normalizeGseaResult } from '../gseaHelpers';
import { GseaDiseaseEnrichmentDTO, GseaParams } from '../types';

interface UseGseaAPIProps {
  authorization: string;
  isAuthenticated: boolean;
  gseaParams: GseaParams;
}

export const useGseaAPI = ({ authorization, isAuthenticated, gseaParams }: UseGseaAPIProps) => {
  const [gseaLoading, setGseaLoading] = useState(false);
  const [gseaResults, setGseaResults] = useState<GseaDiseaseEnrichmentDTO[] | null>(null);
  const [gseaCsvData, setGseaCsvData] = useState<string | null>(null);
  const [gseaMetadataContent, setGseaMetadataContent] = useState<string | null>(null);
  const [showGseaErrorAlert, setShowGseaErrorAlert] = useState(false);
  const [showGseaEmptyResultsAlert, setShowGseaEmptyResultsAlert] = useState(false);
  const [showGseaSuccessAlert, setShowGseaSuccessAlert] = useState(false);
  const [showGseaApiKeyAlert, setShowGseaApiKeyAlert] = useState(false);
  const [showGseaTrialLimitModal, setShowGseaTrialLimitModal] = useState(false);

  const performGsea = async (text: string, geneFilterType: string, paramsOverride?: GseaParams) => {
    if (!isAuthenticated) {
      setShowGseaApiKeyAlert(true);
      return null;
    }

    const paramsToUse = paramsOverride ?? gseaParams;

    setGseaLoading(true);
    setShowGseaErrorAlert(false);
    setShowGseaEmptyResultsAlert(false);
    setShowGseaSuccessAlert(false);

    try {
      trackEnrichmentAction('gsea_started', `gsea_${text.split('\n').length}_items`);

      const formatFilterType = geneFilterType.replace('geneSymbol', 'symbol').replace('name', 'symbol') as
        | 'symbol'
        | 'ensemblId'
        | 'geneId'
        | 'uniprotId';

      const payload = getGseaPayload(text, { ...paramsToUse }, formatFilterType);
      const baseUrl = `${process.env.REACT_APP_URL_BACKEND_API}api/v1`;
      const endpoint = `${baseUrl}/enrichment/gsea`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: authorization as string,
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 402) {
        setShowGseaTrialLimitModal(true);
        return null;
      }

      if (!response.ok) {
        throw new Error(`GSEA request failed: ${response.status}`);
      }

      const jsonData = await response.json();

      if (jsonData.status === 'BAD_REQUEST' || jsonData.error) {
        const errorMessage = jsonData.error?.message || jsonData.error?.details || 'Unknown error';
        throw new Error(errorMessage);
      }

      const rawPayload = Array.isArray(jsonData.payload) ? jsonData.payload : [];
      const normalized = rawPayload.map((item: Record<string, unknown>) => normalizeGseaResult(item));

      if (normalized.length === 0) {
        setGseaResults(null);
        setGseaCsvData(null);
        setGseaMetadataContent(null);
        setShowGseaEmptyResultsAlert(true);
        trackEnrichmentAction('gsea_empty', 'gsea_no_results');
        return null;
      }

      setGseaResults(normalized);
      setShowGseaSuccessAlert(true);
      trackEnrichmentAction('gsea_completed', `gsea_${normalized.length}_results`);

      const metadataContent = createGseaInfoContent({
        text,
        gseaParams: { ...paramsToUse },
        geneFilterType,
      });
      setGseaMetadataContent(metadataContent);

      const csvData = buildGseaCsv(normalized);
      setGseaCsvData(csvData);

      return {
        gseaData: normalized,
        csvData,
        metadataData: metadataContent,
      };
    } catch (error) {
      console.error('GSEA error:', error);
      setShowGseaErrorAlert(true);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      trackEnrichmentAction('gsea_failed', `gsea_${errorMessage}`);
      return null;
    } finally {
      setGseaLoading(false);
    }
  };

  const clearGseaResults = () => {
    setGseaResults(null);
    setGseaCsvData(null);
    setGseaMetadataContent(null);
    setShowGseaSuccessAlert(false);
    setShowGseaErrorAlert(false);
    setShowGseaEmptyResultsAlert(false);
  };

  const dismissGseaAlerts = () => {
    setShowGseaSuccessAlert(false);
    setShowGseaErrorAlert(false);
    setShowGseaEmptyResultsAlert(false);
  };

  return {
    gseaLoading,
    gseaResults,
    gseaCsvData,
    gseaMetadataContent,
    showGseaErrorAlert,
    showGseaEmptyResultsAlert,
    showGseaSuccessAlert,
    showGseaApiKeyAlert,
    showGseaTrialLimitModal,
    performGsea,
    clearGseaResults,
    dismissGseaAlerts,
    setShowGseaApiKeyAlert,
    setShowGseaTrialLimitModal,
    restoreGseaResults: (
      results: GseaDiseaseEnrichmentDTO[],
      csvData: string | null = null,
      metadataContent: string | null = null,
    ) => {
      setGseaResults(results);
      setGseaCsvData(csvData);
      setGseaMetadataContent(metadataContent);
      setShowGseaErrorAlert(false);
      setShowGseaEmptyResultsAlert(false);
    },
  };
};

function buildGseaCsv(results: GseaDiseaseEnrichmentDTO[]): string {
  const headers = [
    'diseaseId',
    'diseaseName',
    'es',
    'nes',
    'pvalue',
    'fdr',
    'matchingGenes',
    'geneSetSize',
    'leadingEdgeSize',
    'leadingEdge',
  ];

  const rows = results.map(item =>
    [
      item.diseaseId,
      item.diseaseName,
      item.es,
      item.nes,
      item.pvalue,
      item.fdr,
      item.matchingGenes,
      item.geneSetSize,
      item.leadingEdgeSize,
      `"${item.leadingEdge.join(',')}"`,
    ].join(','),
  );

  return [headers.join(','), ...rows].join('\n');
}
